import isEqual from 'lodash.isequal';
import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import logCustomRouteEditorUndoCtaTapped from '@alltrails/amplitude/events/logCustomRouteEditorUndoCtaTapped';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logCustomRouteUpdateFailed from '@alltrails/amplitude/events/logCustomRouteUpdateFailed';
import useUser from '@alltrails/context/hooks/useUser';
import { useAppDispatch, useAppSelector } from '../../redux';
import {
  closeLoopActiveSegment,
  doubleBackActiveSegment,
  redo as redoAction,
  removeActiveSegmentHandle,
  reverseActiveSegment,
  reverseCustomRoute,
  selectCustomRouteActiveSegment,
  setCutIndex,
  setSelectedHandleIndex,
  setToastMessage,
  splitActiveSegmentAtSelectedHandle,
  undo as undoAction,
  updateRouting
} from '../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../redux/customRoutesApi';
import { isSegmentRoutable } from '../../utils/customRoutes/validation';
import useRerouteSegments from './useRerouteSegments';
import useRouting from './useRouting';

export type ActionSource = 'toolbar' | 'contextMenu' | 'keyboard';

const useSegmentActions = () => {
  const intl = useIntl();
  const user = useUser();
  const userHasByotEntitlement = user?.featureEntitlements?.includes('build_your_own_trail');
  const dispatch = useAppDispatch();
  const { isSmartRouting, segments, selectedHandleIndex, undoStack, redoStack, isTrailMap, cutData } = useAppSelector(state => ({
    isSmartRouting: state.customRoute.isSmartRouting,
    segments: state.customRoute.route.segments,
    selectedHandleIndex: state.customRoute.selectedHandleIndex,
    undoStack: state.customRoute.undoStack,
    redoStack: state.customRoute.redoStack,
    isTrailMap: state.customRoute.isTrailMap,
    cutData: state.customRoute.cutData
  }));

  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);

  const routing = useRouting();
  const rerouteSegments = useRerouteSegments();
  const isDoubleBackingRef = useRef(false);

  const isUndoDisabled = useMemo(() => !undoStack || undoStack.length === 0, [undoStack]);
  const isRedoDisabled = useMemo(() => !redoStack || redoStack.length === 0, [redoStack]);

  const { isReverseSegmentDisabled, isDoubleBackDisabled, isCloseLoopDisabled } = useMemo(() => {
    const firstPath = activeSegment?.paths[0];
    const firstPoint = firstPath?.points[0];
    const lastPath = activeSegment?.paths[activeSegment.paths.length - 1];
    const lastPoint = lastPath?.points[lastPath.points.length - 1];

    const isRoutable = activeSegment && isSegmentRoutable(activeSegment);
    const hasDifferentStartEnd = isRoutable && !isEqual(firstPoint, lastPoint);

    return {
      isReverseSegmentDisabled: !isRoutable,
      isDoubleBackDisabled: !hasDifferentStartEnd,
      isCloseLoopDisabled: !hasDifferentStartEnd
    };
  }, [activeSegment]);

  const isReverseRouteDisabled = useMemo(() => {
    if (!userHasByotEntitlement) {
      // Only allow peak users to see the option to reverse the entire route
      return true;
    }

    if (segments.length === 1 && activeSegment) {
      // When there is only one segment and its active, disable reversing the entire route in favor of reversing the selected segment
      return true;
    }
    return segments.length === 0 || segments.some(s => !isSegmentRoutable(s));
  }, [activeSegment, segments, userHasByotEntitlement]);

  const isCutSegmentDisabled = useMemo(() => {
    return (
      selectedHandleIndex === undefined || selectedHandleIndex === 0 || selectedHandleIndex === activeSegment?.paths.length || cutData !== undefined
    );
  }, [selectedHandleIndex, cutData, activeSegment]);

  const undo = useCallback(
    (source: ActionSource) => {
      if (isUndoDisabled) {
        return;
      }

      dispatch(undoAction());

      // The event is a CTA tap, so a keyboard shortcut does not count as one
      if (!isTrailMap && source !== 'keyboard') {
        logCustomRouteEditorUndoCtaTapped();
      }
    },
    [dispatch, isUndoDisabled, isTrailMap]
  );

  const redo = useCallback(
    (source: ActionSource) => {
      if (isRedoDisabled) {
        return;
      }

      dispatch(redoAction());

      if (!isTrailMap) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'revert undo',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
          smart_route_menu_activity: source === 'toolbar'
        });
      }
    },
    [dispatch, isRedoDisabled, isSmartRouting, isTrailMap]
  );

  const reverseSegment = useCallback(
    (source: ActionSource) => {
      if (!activeSegment) {
        return;
      }

      const hasNonFreeDrawnPaths = activeSegment.paths.some(path => !path.freeDraw);
      dispatch(reverseActiveSegment());

      if (hasNonFreeDrawnPaths) {
        dispatch(
          setToastMessage({
            iconCode: 'error',
            message: intl.formatMessage({ defaultMessage: 'You may be routed on one-way paths — check your route carefully' })
          })
        );
      }

      if (!isTrailMap) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'reverse direction',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
          smart_route_menu_activity: source === 'toolbar'
        });
      }
    },
    [activeSegment, dispatch, isSmartRouting, intl, isTrailMap]
  );

  const reverseRoute = useCallback(
    (source: ActionSource) => {
      const hasNonFreeDrawnPaths = segments.some(activeSegment => activeSegment.paths.some(path => !path.freeDraw));
      dispatch(reverseCustomRoute());

      if (hasNonFreeDrawnPaths) {
        dispatch(
          setToastMessage({
            iconCode: 'error',
            message: intl.formatMessage({ defaultMessage: 'You may be routed on one-way paths — check your route carefully' })
          })
        );
      }

      logCustomRouteUpdated({
        custom_route_action_taken: 'reverse direction',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: source === 'toolbar'
      });
    },
    [segments, dispatch, isSmartRouting, intl]
  );

  const doubleBack = useCallback(
    async (source: ActionSource) => {
      if (!activeSegment || isDoubleBackingRef.current) {
        return;
      }

      const hasNonFreeDrawnPaths = activeSegment.paths.some(path => !path.freeDraw);
      if (hasNonFreeDrawnPaths && !isTrailMap) {
        isDoubleBackingRef.current = true;
        // Create the double-back segment with return paths for API re-routing
        const returnPaths = [...activeSegment.paths.map(path => ({ ...path, points: [...path.points] }))];
        returnPaths.reverse();
        returnPaths.forEach(path => {
          path.points.reverse();
        });

        const doubleBackSegment = {
          ...activeSegment,
          paths: [...activeSegment.paths, ...returnPaths]
        };

        const result = await rerouteSegments({ segments: [doubleBackSegment] });
        if (result.isSuccess && result.data?.segments?.[0]) {
          dispatch(updateRouting({ segment: result.data.segments[0] }));
        } else {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          dispatch(doubleBackActiveSegment());
        }

        isDoubleBackingRef.current = false;
      } else {
        // For free-drawn paths, use the local double back logic
        dispatch(doubleBackActiveSegment());
      }

      if (!isTrailMap) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'double back',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
          smart_route_menu_activity: source === 'toolbar'
        });
      }
    },
    [activeSegment, dispatch, isSmartRouting, rerouteSegments, isTrailMap]
  );

  const closeLoop = useCallback(
    async (source: ActionSource) => {
      if (!activeSegment) {
        return;
      }

      let points;
      if (isSmartRouting) {
        const startPoint = activeSegment.paths[0].points[0];
        const endPath = activeSegment.paths[activeSegment.paths.length - 1];
        const endPoint = endPath.points[endPath.points.length - 1];
        const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes([endPoint, startPoint]) });

        points = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;
        if (!isSuccess || !points) {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          return;
        }
      }

      dispatch(closeLoopActiveSegment(points));

      if (!isTrailMap) {
        logCustomRouteUpdated({
          custom_route_action_taken: 'close loop',
          custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
          smart_route_menu_activity: source === 'toolbar'
        });
      }
    },
    [isSmartRouting, activeSegment, routing, dispatch, isTrailMap]
  );

  const cutSegment = useCallback(async () => {
    if (selectedHandleIndex === undefined || selectedHandleIndex === 0 || selectedHandleIndex === activeSegment?.paths.length) {
      return;
    }

    dispatch(setCutIndex(selectedHandleIndex));
  }, [dispatch, selectedHandleIndex, activeSegment]);

  const selectStartPoint = useCallback(() => {
    if (!activeSegment) {
      return;
    }

    dispatch(setSelectedHandleIndex(0));
  }, [activeSegment, dispatch]);

  const selectEndPoint = useCallback(() => {
    if (!activeSegment) {
      return;
    }

    dispatch(setSelectedHandleIndex(activeSegment.paths.length));
  }, [activeSegment, dispatch]);

  const removePoint = useCallback(async () => {
    if (!activeSegment || selectedHandleIndex === undefined) {
      return;
    }

    let replacementPoints;
    const isStart = selectedHandleIndex === 0;
    const isEnd = selectedHandleIndex === activeSegment.paths.length;
    if (!isStart && !isEnd) {
      const prevPath = activeSegment.paths[selectedHandleIndex - 1];
      const path = activeSegment.paths[selectedHandleIndex];
      const prevHandle = prevPath.points[0];
      const nextHandle = path.points[path.points.length - 1];

      // Only route if the handles before and after the handle being removed are different and at least one of the paths connected to the handle being removed is not free draw
      if (!isEqual(prevHandle, nextHandle) && (!prevPath.freeDraw || !path.freeDraw)) {
        const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes([prevHandle, nextHandle]) });
        replacementPoints = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;

        if (!isSuccess || !replacementPoints) {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          return;
        }
      }
    }

    dispatch(removeActiveSegmentHandle(replacementPoints));
  }, [activeSegment, selectedHandleIndex, routing, dispatch]);

  const splitSegmentAtHandle = useMemo(() => {
    if (!userHasByotEntitlement || !activeSegment) {
      return;
    }

    return () => dispatch(splitActiveSegmentAtSelectedHandle());
  }, [activeSegment, dispatch, userHasByotEntitlement]);

  return {
    isUndoDisabled,
    isRedoDisabled,
    isReverseSegmentDisabled,
    isReverseRouteDisabled,
    isDoubleBackDisabled: isDoubleBackDisabled || isDoubleBackingRef.current,
    isCloseLoopDisabled,
    isCutSegmentDisabled,
    undo,
    redo,
    reverseSegment,
    reverseRoute,
    doubleBack,
    closeLoop,
    selectStartPoint,
    selectEndPoint,
    removePoint,
    splitSegmentAtHandle,
    cutSegment
  };
};

export default useSegmentActions;
